import React, { useState } from 'react';
import { Copy, Check, Tag, ArrowUpRight, Contact, MapPin } from 'lucide-react';
export type TabId = 'social' | 'personal' | 'notes';
interface InfluencerProfileProps {
  activeSection?: string | null;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  onSectionClick?: (section: string) => void;
}
export const InfluencerProfile: React.FC<InfluencerProfileProps> = ({
  activeSection,
  activeTab: controlledTab,
  onTabChange,
  onSectionClick
}) => {
  const [copied, setCopied] = useState(false);
  const [internalTab, setInternalTab] = useState<TabId>('social');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: TabId) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const handleCopy = () => {
    const email = 'john@abctalentagency.com';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(email).catch(() => {});
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  // When a left-menu section is active, lift the matching region into a white
  // glassy popup and blur/dim everything else.
  const getHighlightClass = (section: string) => {
    if (!activeSection) return 'transition-all duration-300 rounded-lg';
    return activeSection === section ? 'relative z-20 bg-white/80 backdrop-blur-md rounded-xl ring-1 ring-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-3 -m-1 scale-[1.02] transition-all duration-300' : 'blur-[2px] opacity-40 transition-all duration-300';
  };
  const tabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'social',
    label: 'Social Media'
  }, {
    id: 'personal',
    label: 'Personal Information'
  }, {
    id: 'notes',
    label: 'Notes'
  }];
  return <div className="w-full bg-white rounded-xl border border-gray-200 shadow-float overflow-hidden text-left font-sans text-sm" data-id="element-592" data-mp="2nemksy8kgvp4">
      {/* Header */}
      <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 relative" data-id="element-593" data-mp="1bxmu271vaox7">
        <div className={`flex items-center gap-4 ${getHighlightClass('influencer')}`} onClick={() => onSectionClick?.('influencer')} data-id="element-594" data-mp="rxcu998cac1l">
          <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-596.png" alt="Michelle Brown" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover" data-id="element-595" data-mp="j1wxajcafgrs" />
          <div data-id="element-596" data-mp="92edc2wdnuzq">
            <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-597" data-mp="45xp78p6hgmn">
              Michelle Brown
            </h3>
            <div className="flex items-center gap-2 mb-1" data-id="element-598" data-mp="3kj2j6irgpc">
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3 h-3" data-id="element-599" data-mp="66skocz06a7" />
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3 h-3" data-id="element-600" data-mp="1ufr6qhglnpyj" />
            </div>
            <p className="text-xs text-gray-600 mb-2" data-id="element-601" data-mp="221330luyp1o3">
              Paid Brand Promo • Affiliate
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-602" data-mp="26te59vd6czjt">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FBEFC8] text-[#BE8725] text-[10px] font-medium" data-id="element-603" data-mp="26upalzm9qfd1">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-579.png" alt="US Flag" className="w-3 h-2 rounded-sm" data-id="element-604" data-mp="1e4h0mv1vz19j" />
                United States
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#FCE1C8] text-[#EB7535] text-[10px] font-medium" data-id="element-605" data-mp="1s36ywe0fz6zh">
                Influencer
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#EAD2EE] text-[#A971B1] text-[10px] font-medium" data-id="element-606" data-mp="2zjcwnxeud8rn">
                Fashion
              </span>
            </div>
          </div>
        </div>

        <div className={`text-left md:text-right bg-gray-50 p-3 rounded-lg border border-gray-100 relative ${getHighlightClass('agency')}`} onClick={() => onSectionClick?.('agency')} data-id="element-607" data-mp="2x2looa91h1f5">
          {/* Highlight indicator for CTA */}
          <div className="absolute -top-3 -right-3 bg-zano-pink text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md flex items-center gap-1 animate-bounce" data-id="element-608" data-mp="36z87yulpvkf2">
            Contact Manager <ArrowUpRight size={12} data-id="element-609" data-mp="3g0odop1l6ycy" />
          </div>

          <h4 className="text-base font-bold text-gray-900 mb-1" data-id="element-610" data-mp="3uqnc8sx20bsy">
            ABC Talent Agency
          </h4>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold mb-1.5" data-id="element-611" data-mp="3pqr88tocwkde">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-612" data-mp="7jbl6vsmtxzx" />
            Exclusive representation
          </span>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-613" data-mp="m8cwet21tpax">John Marcus</p>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-614" data-mp="m60fmj811kzg">+1-818-444-2311</p>
          <div className="text-xs text-gray-600 flex items-center md:justify-end gap-1.5 mt-1" data-id="element-615" data-mp="r5wnitjuhwka">
            <span className="font-medium text-zano-charcoal" data-id="element-616" data-mp="d6n6jrnteyf9">
              john@abctalentagency.com
            </span>
            <button onClick={handleCopy} aria-label="Copy agency email" className={`p-1 rounded-md transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-white border border-gray-200 text-gray-500 hover:text-zano-pink hover:border-zano-pink'}`} data-id="element-617" data-mp="i4op8bhbwvq7">
              {copied ? <Check size={12} data-id="element-618" data-mp="3qki7aurb6f9d" /> : <Copy size={12} data-id="element-619" data-mp="3unes8s4bmngh" />}
            </button>
          </div>
          {copied && <p className="text-[10px] text-green-600 font-medium mt-0.5 md:text-right absolute right-3 -bottom-4" data-id="element-620" data-mp="3gof55owgigmf">
              Copied!
            </p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-50/50 border-b border-gray-100 px-4 md:px-6 flex gap-6 overflow-x-auto" data-id="element-621" data-mp="1r7jk7sg29yu6">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-pressed={activeTab === tab.id} className={`py-3 text-xs whitespace-nowrap uppercase tracking-wide border-b-2 transition-colors ${activeTab === tab.id ? 'font-bold text-zano-pink border-zano-pink' : 'font-semibold text-gray-500 border-transparent hover:text-gray-700'}`} data-id="element-622" data-mp="1mfi23pw7pdw8">
            {tab.label}
          </button>)}
      </div>

      {/* Personal Information tab */}
      {activeTab === 'personal' && <div className="p-4 md:p-6 space-y-6" data-id="element-623" data-mp="1w636jtykhcn2">
          <div data-id="element-624" data-mp="1w5bbqlcsnixu">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-625" data-mp="1w5uu236cw9ce">
              <Contact size={14} className="text-gray-800" data-id="element-626" data-mp="pqameyi9xz08" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-627" data-mp="pno8mh4sin9k">
                Basic Info
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-628" data-mp="anixhwmbqbf9">
              <div data-id="element-629" data-mp="flkf0d43q2om">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-630" data-mp="fmvmamwr9skh">Name</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-631" data-mp="kknb3823sykg">
                  Marsha Glonen
                </p>
              </div>
              <div data-id="element-632" data-mp="pdgi62l1oizp">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-633" data-mp="pe021ou99hgd">Languages</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-634" data-mp="tcz14stb2jzw">
                  English, Spanish
                </p>
              </div>
              <div data-id="element-635" data-mp="13bolcs8ocgjb">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-636" data-mp="13b54leurfy6v">Gender</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-637" data-mp="18arfvir134d1">Female</p>
              </div>
              <div data-id="element-638" data-mp="188ezha0x5223">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-639" data-mp="353b4k7mbr758">Age</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-640" data-mp="3554a1jl7blbn">28</p>
              </div>
            </div>
          </div>
          <div data-id="element-641" data-mp="1fu0ovavmh11k">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-642" data-mp="1ftikfqyt3gik">
              <MapPin size={14} className="text-gray-800" data-id="element-643" data-mp="1frxp3z4o3tjn" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-644" data-mp="1auedz839nwvx">Location</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-645" data-mp="1piscx7xkb177">
              <div data-id="element-646" data-mp="1kmbosdqd4mu4">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-647" data-mp="1klshdqyc9guc">Country</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-648" data-mp="wwhg88d2tkfo">
                  United States
                </p>
              </div>
              <div data-id="element-649" data-mp="ryfxxpnh5p59">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-650" data-mp="15w4cyguv222e">State</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-651" data-mp="15u019cqpzyrk">California</p>
              </div>
              <div data-id="element-652" data-mp="11wlv5tyc673s">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-653" data-mp="2hb9j5uu7aiqr">City</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-654" data-mp="2h9geum728alx">Los Angeles</p>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 italic" data-id="element-655" data-mp="2kt74vo3e86a9">
            Personal information is filled out and maintained by the talent
            agency.
          </p>
        </div>}

      {/* Notes tab */}
      {activeTab === 'notes' && <div className="p-4 md:p-6" data-id="element-656" data-mp="2awkhosnlh352">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-3" data-id="element-657" data-mp="2axdotuqgtunq">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-658" data-mp="2fw6y1aypyzi3">
              Talent manager notes
            </p>
            <p className="text-xs text-gray-700 leading-relaxed" data-id="element-659" data-mp="38j1cnr8uv4nn">
              Michelle Brown is a Los Angeles-based fashion and lifestyle
              creator known for her polished try-on hauls and authentic GRWM
              content. She over-indexes with a Gen-Z and young-millennial female
              audience in the US and UK, and consistently drives strong TikTok
              Shop conversion.
            </p>
            <div data-id="element-660" data-mp="3ra1552fk6wlo">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-661" data-mp="3raki04qcqusu">
                Notable collaborations
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-662" data-mp="szggdwkdn1rj">
                Revolve (summer capsule launch), Sephora Squad, Fashion Nova,
                and a recurring ambassador partnership with a DTC skincare brand
                across 3 campaigns.
              </p>
            </div>
            <div data-id="element-663" data-mp="5ae12vd7tl07">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-664" data-mp="59vvv98krwg5">
                Awards and recognition
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-665" data-mp="a2f9g679irky">
                Finalist, 2024 Creator Economy Awards (Fashion). Featured in
                TikTok Rising Creators to Watch list.
              </p>
            </div>
            <div data-id="element-666" data-mp="166cqzao672i">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-667" data-mp="3igxjgdgrkh77">
                Searchable keywords
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-668" data-mp="3nepgflw9mti9">
                {['fashion', 'try-on haul', 'GRWM', 'TikTok Shop', 'affiliate', 'skincare', 'Gen-Z', 'US', 'UK', 'brand ambassador'].map(kw => <span key={kw} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded text-[10px] font-medium" data-id="element-669" data-mp="38sa7p9uvurim">
                    {kw}
                  </span>)}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 italic pt-1" data-id="element-670" data-mp="bx8hc32unmh">
              Notes are written by the talent manager. Keywords here power
              search when brands and agencies query the database.
            </p>
          </div>
        </div>}

      {/* Social Media tab */}
      {activeTab === 'social' && <div className="p-4 md:p-6 space-y-6" data-id="element-671" data-mp="5a8hjkljsu3n">
          {/* Tags */}
          <div className={getHighlightClass('influencer')} onClick={() => onSectionClick?.('influencer')} data-id="element-672" data-mp="cgug7fi4apf0">
            <div className="flex items-center gap-1.5 mb-2" data-id="element-673" data-mp="hc9qxyktw577">
              <Tag size={14} className="text-gray-800" data-id="element-674" data-mp="mc4js7o5z4uw" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-675" data-mp="r45uvq9lipjc">
                Content Style
              </h4>
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-676" data-mp="w2qsdhxgqv43">
              {['TikTok Shop', 'GRWM', 'Try-on Hauls'].map(tag => <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium" data-id="element-677" data-mp="103at0vh8wguz">
                  {tag}
                </span>)}
            </div>
          </div>

          <hr className="border-gray-100" data-id="element-678" data-mp="36wd86xym2ye2" />

          {/* TikTok Section */}
          <section className={getHighlightClass('analytics')} onClick={() => onSectionClick?.('analytics')} data-id="element-679" data-mp="3buem74h6k7io">
            <div className="flex items-center justify-between mb-4" data-id="element-680" data-mp="34cowlbtjh205">
              <div className="flex items-center gap-1.5" data-id="element-681" data-mp="2zk5hgaf7dyt8">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-4 h-4" data-id="element-682" data-mp="2ziko2o4mklwy" />
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-683" data-mp="3n9q16vj6l03k">TikTok</h4>
              </div>
              <span className="text-xs text-gray-600" data-id="element-684" data-mp="3ib6fmw8i1qbq">@michellethereal</span>
            </div>

            <div className="space-y-6" data-id="element-685" data-mp="3dbtwalgu71dw">
              {/* Analytics */}
              <div data-id="element-686" data-mp="3ddwu3ro8dyt6">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-687" data-mp="38cqy9kz9xvff">
                  Live Analytics
                </h5>
                <div className="grid grid-cols-4 gap-2" data-id="element-688" data-mp="21xgd3tx13a47">
                  <div data-id="element-689" data-mp="1wxtzhjdafruv">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-690" data-mp="1wwirf7ct7xxy">
                      Followers
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-691" data-mp="3lqge1ehs628h">2.2M</p>
                  </div>
                  <div data-id="element-692" data-mp="3qjsp0ltjeair">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-693" data-mp="3qkarw1apfmzn">
                      AVG Views
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-694" data-mp="3gs55d7381xru">50K</p>
                  </div>
                  <div data-id="element-695" data-mp="8cbq0vkmmscd">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-696" data-mp="8e6a67o2qgfe">
                      AVG Likes
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-697" data-mp="db6diatjh52t">4.5K</p>
                  </div>
                  <div data-id="element-698" data-mp="3um5v5bc74ag2">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-699" data-mp="3ds2wruaraif">ENG Rate</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-700" data-mp="3fuwflunrlrw">5.3%</p>
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div data-id="element-701" data-mp="2o5kahwnjf3jj">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-702" data-mp="2o1mqz9x1s9z4">
                  Audience Demographics
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-703" data-mp="116hutvf6u106">
                  {/* Gender */}
                  <div data-id="element-704" data-mp="1f5r8anc0eh0g">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-705" data-mp="1f4ybof6rl7w1">Gender</p>
                    <div className="flex items-center gap-3" data-id="element-706" data-mp="1b5yzqamm8av3">
                      <div className="relative w-16 h-16 rounded-full shrink-0" style={{
                    background: 'conic-gradient(#77CCEA 0% 50%, #FF9CBE 50% 100%)'
                  }} data-id="element-707" data-mp="nfd4jjxpml2i">
                        <div className="absolute inset-[8px] bg-white rounded-full" data-id="element-708" data-mp="rh81ydciseb0"></div>
                      </div>
                      <div className="space-y-1 text-xs w-full" data-id="element-709" data-mp="3qktnqyozmnx">
                        <div className="flex items-center justify-between gap-2" data-id="element-710" data-mp="3vvy7ok3nujxp">
                          <div className="flex items-center gap-1.5" data-id="element-711" data-mp="1yv21lrr9qmoy">
                            <span className="w-2 h-2 rounded-full bg-[#77CCEA]" data-id="element-712" data-mp="1yusau1yegbi7"></span>
                            <span className="text-gray-600" data-id="element-713" data-mp="23skbplh0v7yj">Male</span>
                          </div>
                          <span className="font-medium" data-id="element-714" data-mp="28ldg288kwea3">50%</span>
                        </div>
                        <div className="flex items-center justify-between gap-2" data-id="element-715" data-mp="2djez4t608dlk">
                          <div className="flex items-center gap-1.5" data-id="element-716" data-mp="1fx98fh6g5r4r">
                            <span className="w-2 h-2 rounded-full bg-[#FF9CBE]" data-id="element-717" data-mp="1kv0ye5nj736b"></span>
                            <span className="text-gray-600" data-id="element-718" data-mp="1pudnyy5seocl">Female</span>
                          </div>
                          <span className="font-medium" data-id="element-719" data-mp="1ut712ph5wdc8">50%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Country */}
                  <div data-id="element-720" data-mp="368djzy1ihtuk">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-721" data-mp="367kltlgmcco1">Country</p>
                    <div className="space-y-2" data-id="element-722" data-mp="uls5s65v8jlr">
                      {[{
                    name: '🇺🇸 US',
                    val: 60
                  }, {
                    name: '🇬🇧 UK',
                    val: 30
                  }, {
                    name: '🇩🇪 DE',
                    val: 10
                  }].map(c => <div key={c.name} data-id="element-723" data-mp="6v69fc584nrl">
                          <div className="flex justify-between text-[10px] mb-0.5" data-id="element-724" data-mp="6uec9wvzahz7">
                            <span className="text-gray-700" data-id="element-725" data-mp="1xe9pvyc7xcu">{c.name}</span>
                            <span className="font-medium" data-id="element-726" data-mp="3t3pf24adrk9z">{c.val}%</span>
                          </div>
                          <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-727" data-mp="1wavuxcb9si4p">
                            <div className="h-full bg-[#E66792] rounded-full" style={{
                        width: `${c.val}%`
                      }} data-id="element-728" data-mp="1rb9ktbqqni04"></div>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Age */}
                  <div data-id="element-729" data-mp="vdvuuzy6tk2h">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-730" data-mp="vd42rz3su4gy">Age</p>
                    <div className="h-20 flex items-end gap-1.5 pb-4 border-b border-gray-200 relative" data-id="element-731" data-mp="he4fovvxpxmw">
                      {[{
                    label: '13-17',
                    val: 4
                  }, {
                    label: '18-24',
                    val: 40
                  }, {
                    label: '25-34',
                    val: 20
                  }, {
                    label: '35-44',
                    val: 10
                  }, {
                    label: '45+',
                    val: 9
                  }].map(a => <div key={a.label} className="flex-1 flex flex-col items-center gap-0.5" data-id="element-732" data-mp="4z6ddi5wzgai">
                          <span className="text-[8px] font-medium text-gray-600" data-id="element-733" data-mp="dw6v55yhag4z">
                            {a.val}%
                          </span>
                          <div className="w-full max-w-[8px] bg-[#53B7AB] rounded-t-sm" style={{
                      height: `${a.val}px`
                    }} data-id="element-734" data-mp="nvpcrfqx1ptv"></div>
                          <span className="absolute bottom-0 text-[8px] text-gray-500 translate-y-full pt-0.5" data-id="element-735" data-mp="2ppexocf2zy0c">
                            {a.label}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-gray-100" data-id="element-736" data-mp="pwezk4u64tfg" />

          {/* Pricing */}
          <div className={getHighlightClass('pricing')} onClick={() => onSectionClick?.('pricing')} data-id="element-737" data-mp="kyn4x3252t7k">
            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-738" data-mp="3teyj8iys6qyn">
              Pricing & Deliverables
            </h5>
            <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-739" data-mp="76yxve6qyuxg">
              <div data-id="element-740" data-mp="78jr90hbs7tq">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-741" data-mp="74w027zcrxi6">Video post</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-742" data-mp="26az26ca19so">
                  $800 - $1,300
                </p>
              </div>
              <div data-id="element-743" data-mp="1dn14hn8vlxl8">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-744" data-mp="1dnu5s8p2fuvz">Link in Bio</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-745" data-mp="2vkxj93dew6jz">$500</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded p-3 border border-gray-100" data-id="element-746" data-mp="30ka5pn0dfbm1">
              <p className="text-[10px] font-semibold text-gray-500 mb-1" data-id="element-747" data-mp="2mjzcwefi3my8">
                Agency Notes
              </p>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed" data-id="element-748" data-mp="2rikd4dur66mo">
                Script - additional $400{'\n'}
                price includes 2 revisions{'\n'}
                additional revisions + $100 each{'\n'}7 days lead time, link in
                bio price for 24 hrs
              </p>
            </div>
          </div>
        </div>}
    </div>;
};